import clone from 'ramda/src/clone'
import { ACTION_TYPES, INPUT_TYPES, STATE_ID, STATE_TYPES } from '@/utils/bot-json.constants'

const ANSWER_REF_REGEX = /ref:answer:([^"]+)/g

export function inlineTemplate(hostJson, templateId, templateJson) {
  const inlined = clone(hostJson)
  if (!inlined.actions) inlined.actions = {}
  if (!inlined.postbacks) inlined.postbacks = {}

  const fromTemplate = `ref:template:${templateId}`
  const templateStates = templateJson?.states || {}
  const templateActions = templateJson?.actions || {}
  const templatePostbacks = templateJson?.postbacks || {}

  addMissingTemplateRefs(inlined.states || {}, templateId, templateStates, templateActions, templatePostbacks)

  for (const [stateId, state] of Object.entries(inlined.states || {})) {
    if (state.type !== STATE_TYPES.TEMPLATE_REF || state.fromTemplate !== fromTemplate) continue

    const templateState = templateStates[state.templateState]
    if (!templateState) {
      throw new Error(`Template state "${state.templateState}" not found for templateRef "${stateId}"`)
    }

    const suffix = state.postbackSuffix || ''
    const newState = clone(templateState)
    newState.transitions = [...(state.templateTransitions || []), ...(state.transitions || [])]
    delete newState.templateTransitions
    if (newState.conditionEndState && templateStates[newState.conditionEndState]) {
      newState.conditionEndState = newState.conditionEndState + suffix
    }
    if (state.requestOutcome) newState.requestOutcome = state.requestOutcome

    if (Array.isArray(newState.actions)) {
      newState.actions = newState.actions.map(actionId => {
        const newActionId = actionId + suffix
        const templateAction = templateActions[actionId]
        if (!templateAction) {
          throw new Error(`Template action "${actionId}" not found for templateRef "${stateId}"`)
        }
        if (!inlined.actions[newActionId]) {
          inlined.actions[newActionId] = suffixPostbacksInAction(clone(templateAction), suffix)
        }
        copyTemplatePostbacks(inlined.postbacks, templatePostbacks, templateAction, suffix)
        return newActionId
      })
    }

    delete newState.fromTemplate
    delete newState.templateState
    delete newState.postbackSuffix
    inlined.states[stateId] = newState
  }

  return inlined
}

export function collectAnswerRefCreates(templateJson) {
  return findAnswerRefIds(templateJson)
}

export function findAnswerRefIds(convJson) {
  const ids = new Set()
  const matches = Array.from(JSON.stringify(convJson || {}).matchAll(ANSWER_REF_REGEX))
  matches.forEach(match => ids.add(match[1]))
  return Array.from(ids)
}

// A usage only has templateRef states that existed when the block was used or that block edits synced into it.
// Add refs for template states reachable from the usage but missing in it, so inlined postbacks and transitions have a target.
function addMissingTemplateRefs(hostStates, templateId, templateStates, templateActions, templatePostbacks) {
  const fromTemplate = `ref:template:${templateId}`
  const queue = Object.values(hostStates).filter(state => state.type === STATE_TYPES.TEMPLATE_REF && state.fromTemplate === fromTemplate)

  while (queue.length) {
    const ref = queue.pop()
    const templateState = templateStates[ref.templateState]
    if (!templateState) continue

    const suffix = ref.postbackSuffix || ''
    for (const targetStateId of findTargetStateIds(templateState, templateActions, templatePostbacks)) {
      if (!templateStates[targetStateId] || hostStates[targetStateId + suffix]) continue
      const newRef = createTemplateRef(fromTemplate, targetStateId, templateStates[targetStateId], suffix)
      hostStates[targetStateId + suffix] = newRef
      queue.push(newRef)
    }
  }
}

function findTargetStateIds(templateState, templateActions, templatePostbacks) {
  const targets = (templateState.transitions || []).map(transition => transition.state)
  if (templateState.conditionEndState) targets.push(templateState.conditionEndState)
  for (const actionId of templateState.actions || []) {
    const payload = templateActions[actionId]?.content?.payload
    const structures = Array.isArray(payload) ? payload : [payload]
    for (const structure of structures) {
      for (const button of structure?.buttons || []) {
        if (button.type === 'postback' && button.payload) targets.push(templatePostbacks[button.payload])
      }
    }
  }
  return targets.filter(target => typeof target === 'string' && !target.startsWith('ref:'))
}

// Mirrors the templateRef that AnswerController.useTemplate creates for a template state
function createTemplateRef(fromTemplate, templateStateId, templateState, suffix) {
  const templateTransitions = (templateState.transitions || []).map(transition => ({
    ...clone(transition),
    state: transition.state.startsWith('ref:') ? transition.state : transition.state + suffix
  }))
  const hasDefaultTransition = (templateState.transitions || []).some(transition =>
    [STATE_ID.REF_GLOBAL_FIND_INTENT, STATE_ID.REF_GLOBAL_IDLE].includes(transition.state) ||
    [INPUT_TYPES.NONE, INPUT_TYPES.USER_INPUT].includes(transition.condition?.input)
  )
  const ref = {
    type: STATE_TYPES.TEMPLATE_REF,
    fromTemplate,
    templateState: templateStateId,
    postbackSuffix: suffix,
    templateTransitions,
    transitions: hasDefaultTransition ? [] : [{
      state: STATE_ID.REF_GLOBAL_FIND_INTENT,
      condition: { input: INPUT_TYPES.USER_INPUT, expression: { true: {} } }
    }]
  }
  if (templateState.requestOutcome) ref.requestOutcome = templateState.requestOutcome
  return ref
}

function suffixPostbacksInAction(action, suffix) {
  if (!suffix || action.type === ACTION_TYPES.VALIDATION) return action

  const content = action.content
  if (!content) return action

  const structures = Array.isArray(content.payload) ? content.payload : [content.payload]
  for (const structure of structures) {
    if (!structure?.buttons) continue
    for (const button of structure.buttons) {
      if (button.type === 'postback' && button.payload) {
        button.payload = button.payload + suffix
      }
    }
  }

  if (action.id) action.id = action.id + suffix
  return action
}

function copyTemplatePostbacks(hostPostbacks, templatePostbacks, templateAction, suffix) {
  const content = templateAction.content
  if (!content) return

  const structures = Array.isArray(content.payload) ? content.payload : [content.payload]
  for (const structure of structures) {
    if (!structure?.buttons) continue
    for (const button of structure.buttons) {
      if (button.type !== 'postback' || !button.payload) continue
      const originalKey = button.payload
      const suffixedKey = originalKey + suffix
      if (hostPostbacks[suffixedKey] != null) continue
      const postbackValue = templatePostbacks[originalKey]
      if (postbackValue == null) continue
      hostPostbacks[suffixedKey] = typeof postbackValue === 'string' && postbackValue.startsWith('ref:') ?
        postbackValue :
        postbackValue + suffix
    }
  }
}
